/**
 * FAQ-sheet op productpagina's (snippets/sheet-faq.liquid): zijpaneel op desktop, bottom sheet op
 * mobiel. Wordt geopend door de laatste rij van de product-accordeon ([data-sheet-open="faq"]).
 *
 * De sheet heeft geen eigen inhoud. Bij het openen kopieert dit script de HTML van de FAQ-sectie op de
 * pagina (#tabpanel-faq) naar de sheet en initialiseert het de accordeon in die kopie. Op de
 * configurator laadt src/product-configurator.js de FAQ pas bij het scrollen; is de bron nog leeg, dan
 * vraagt dit script via het event `faq-sheet:request` om hem te laden en ververst het de kopie zodra
 * `faq:loaded` binnenkomt (ook bij een wissel tussen kind- en volwassenen-FAQ).
 */

import { initAccordeon } from './lib/section-render.js';

(function () {
  const sheet = document.querySelector('[data-sheet="faq"]');
  if (!sheet) return;

  const panel = sheet.querySelector('[data-sheet-panel]');
  const body = sheet.querySelector('[data-sheet-body]');
  const closeButton = sheet.querySelector('[data-sheet-close]');
  const overlay = sheet.querySelector('[data-sheet-overlay]');
  let isOpen = false;
  let lastTrigger = null;
  let openedByKeyboard = false;

  /**
   * Kopieert de FAQ van de pagina naar de sheet. Vraagt de configurator om de FAQ te laden als de bron
   * nog leeg is.
   */
  function fill() {
    const source = document.querySelector(sheet.dataset.sheetSource);
    const html = source ? source.innerHTML.trim() : '';
    if (!html) {
      body.innerHTML = '';
      document.dispatchEvent(new CustomEvent('faq-sheet:request'));
      return;
    }
    body.innerHTML = html;
    // Geen dubbele id's, en elke vraag start dichtgeklapt (ook als hij op de pagina open staat).
    body.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    body.querySelectorAll('.pt-item').forEach((el) => el.classList.remove('active'));
    body.querySelectorAll('.pt-accordeon-content').forEach((el) => el.removeAttribute('style'));
    initAccordeon(body);
  }

  /**
   * Opent de sheet, vult hem en zet de body-scroll op slot. Via het toetsenbord krijgt de sluitknop de
   * focus; met de muis het paneel zelf, zodat de focus wel in de dialoog zit maar er geen focusring
   * verschijnt.
   * @param {Element|null} trigger - element dat de sheet opende; krijgt de focus terug bij sluiten
   * @param {boolean} viaKeyboard - geopend met Enter/spatie in plaats van een klik
   */
  function open(trigger, viaKeyboard) {
    if (isOpen) return;
    isOpen = true;
    lastTrigger = trigger;
    openedByKeyboard = viaKeyboard;
    fill();
    body.scrollTop = 0;
    sheet.dataset.open = 'true';
    sheet.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    // Pas focussen als de visibility-transitie is gestart; op het eerste frame is het paneel nog hidden.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (isOpen) (viaKeyboard ? closeButton : panel).focus({ preventScroll: true });
    }));
  }

  /**
   * Sluit de sheet. Bij toetsenbordgebruik gaat de focus terug naar de trigger; na muisgebruik wordt hij
   * alleen losgelaten, anders licht de trigger op met een focusring.
   * @param {boolean} viaKeyboard - gesloten met Escape of Enter/spatie op de sluitknop
   */
  function close(viaKeyboard) {
    if (!isOpen) return;
    isOpen = false;
    sheet.dataset.open = 'false';
    sheet.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if ((viaKeyboard || openedByKeyboard) && lastTrigger) {
      lastTrigger.focus({ preventScroll: true });
    } else if (panel.contains(document.activeElement)) {
      document.activeElement.blur();
    }
    lastTrigger = null;
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-sheet-open="faq"]');
    if (!trigger) return;
    event.preventDefault();
    // Een klik die via Enter/spatie op een knop ontstaat heeft detail 0.
    open(trigger, event.detail === 0);
  });
  closeButton.addEventListener('click', (event) => close(event.detail === 0));
  overlay.addEventListener('click', () => close(false));
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && isOpen) close(true);
  });
  // Focus binnen het paneel houden zolang de sheet open is.
  document.addEventListener('focusin', (event) => {
    if (isOpen && !panel.contains(event.target)) closeButton.focus({ preventScroll: true });
  });
  document.addEventListener('faq:loaded', () => {
    if (isOpen) fill();
  });
})();
